import React, { useState, useEffect, useContext, useCallback, createContext, Dispatch, SetStateAction } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAsyncInitialize } from "../utility";
import { config } from "config";
import { UserContext } from "./login";
import { IServiceSpec, userInServiceRoles, UiServices } from "data/service-spec";
import { User } from "data/user";
import { ServiceIndex } from "./service-index";

import "@restspace/schema-form/build/index.css";
import "@restspace/schema-form/src/css/default-skin.css";
import "App.css";
import { editorRegister, IEditorEntry } from "editorSelection/editor-register";
import { IEditorProps } from "./props-interfaces";
import { UnregisterCallback } from "history";
import _ from 'lodash';

function getServices(user: User) {
    const acceptedApis = [ "urlFile", "jsonData", "services", "zipUpload" ];

    return config.api.fetchServices()
        .then(services => ({
            catalogue: services.catalogue,
            active: Object.entries(services.active)
                .filter(([, service]) =>
                    service.apisSupported
                    && acceptedApis.find((api) => service.apisSupported.split(' ').indexOf(api) >= 0)
                    && userInServiceRoles(user, service.manageRoles || service.readRoles)
                    )
                .reduce((services, [basePath, service]) => ({ ...services, [basePath]: service }), {})
            }) as UiServices
        );
}

export interface IDisplay {
    showPane: boolean;
}

export const DisplayContext = createContext<[ IDisplay, Dispatch<SetStateAction<IDisplay>> ]>([ { showPane: false }, () => {} ]);

interface IEditorSelectorProps {
    editors: IEditorEntry[];
    selected: number;
    onSelect: (idx: number) => void;
}

const EditorSelector = (props: IEditorSelectorProps) => {
    const selectIdx = (idx: number) => () => props.onSelect(idx);
    return (
        <ul className="tabs">
            {props.editors.map((ed, idx) => (
                <li key={idx}>
                    <button type="button" className={idx === props.selected ? "selected" : ""} onClick={selectIdx(idx)}>{ed.displayName}</button>
                </li>
            ))}
        </ul>
    );
};

interface PageData {
    text: string;
    mimeType: string;
    serviceName: string;
}

export function EditorPage() {
    const params = useParams();
    const path = params[0];
    let operation = window.location.search;
    operation = operation.startsWith('?') ? operation.substr(1) : operation;
    operation = operation.split('&')[0];
    const [ editors, setEditors ] = useState<IEditorEntry[]>([]);
    const [ editorIdx, setEditorIdx ] = useState(-1);
    const [ unblock, setUnblock ] = useState<UnregisterCallback | null>(null);
    const [ , setDirChangeCount ] = useState(0);
    const [ pageData, setPageData ] = useState<PageData>({ text: '', mimeType: '', serviceName: '' });
    const [ showEditor, setShowEditor ] = useState(false);
    const [ showEditorSelector, setShowEditorSelector ] = useState(false);
    const [ currentEditor, setCurrentEditor ] = useState<JSX.Element | null>(null);
    const history = useHistory();
    const [ user ] = useContext(UserContext);
    const [ display, setDisplay ] = useContext(DisplayContext);
    const services = useAsyncInitialize<UiServices | null>(null, () => getServices(user));

    const onDirChange = useCallback(() => setDirChangeCount((count) => count + 1), []);

    // message to avoid losing changes
    const doOnDirty = useCallback((isDirty: boolean) => {
        if (isDirty && !unblock) {
            const unblocker = history.block('Do you want to throw away your changes?');
            setUnblock(() => unblocker); // setUnblock(unblocker) would wrongly interpret unblocker as a setter function
        } else if (!isDirty && unblock) {
            unblock();
            setUnblock(null);
        }
    }, [ unblock ]);

    useEffect(() => {
        if (services) config.serviceCatalogue = services.catalogue;
    }, [ services ]);

    // reset dirty when moving to new path
    useEffect(() => {
        doOnDirty(false);
    }, [ path ]);

    // reload data
    useEffect(() => {
        config.api.fetchGet(path).then(([text, reqHeaders]) => {
            setPageData({ text, mimeType: reqHeaders.mimeType, serviceName: reqHeaders.serviceName });
        });
    }, [ path, operation, services ]);

    // build a list of the editors required and the props for the current one
    // needs to be split out because doOnDirty can legitimately vary, changing
    // the props of the editor, but this doesn't require a data fetch
    useEffect(() => {
        if (!services || services.active === {}) return;

        if (operation === 'deleted') {
            setEditors([]);
            setEditorIdx(-1);
            return;
        }

        const service: IServiceSpec | null = Object.values(services.active).find(service => service.name === pageData.serviceName) || null;
            
        const editorProps: IEditorProps = {
            path,
            mimeType: pageData.mimeType || '',
            operation,
            onDirty: doOnDirty,
            onDirChange: onDirChange,
            service,
            text: pageData.text || ''
        };

        const newEditors = service
            ? editorRegister.getEditorList(editorProps)
            : [];
        setEditors(newEditors);
        if (!_.isEqual(editors.map(ed => ed.name), newEditors.map(ed => ed.name))) {
            setEditorIdx(newEditors.length ? 0 : -1);
        }
    }, [ onDirChange, doOnDirty, pageData ]);

    // build the current editor
    useEffect(() => {
        setShowEditorSelector(editors.length > 1);
        const newShowEditor = editorIdx >= 0 && editorIdx < editors.length;
        setShowEditor(newShowEditor);
        if (newShowEditor) {
            const editorFC = editorRegister.getEditor(editors[editorIdx].name);
            if (editorFC) {
                setCurrentEditor(React.createElement(editorFC, editors[editorIdx].props));
            }
        }
    }, [ editors, editorIdx, editorRegister ]);

    const onClickNav = () => {
        setDisplay({ showPane: false });
    }

    return (
        <div className={display.showPane ? "App show-pane" : "App"}>
          <div className="data-index">
            <ServiceIndex services={services ? Object.values(services.active) : []} path={path}
            />
          </div>
          <div className={"data-editor" + (showEditorSelector ? " with-tabs" : "")}>
            <nav className='nav-bar'>
                <span className="sf-control-button jd-nav-button" onClick={onClickNav}>x</span>
            </nav>
            {showEditorSelector && <EditorSelector editors={editors} selected={editorIdx} onSelect={setEditorIdx}/>}
            {showEditor && currentEditor}
          </div>
        </div>
      );
}